import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Sparkles, Crown, TrendingDown, Globe2, Zap, Users2, X, Check, Loader2,
} from 'lucide-react';

/**
 * UpgradeToProCard
 *
 * Conversion-focused nudge for the /client/home page. Decides what to show
 * based on the client's current subscription state and their recent usage,
 * because a generic "Upgrade" banner converts poorly — the user needs to see
 * their number.
 *
 *   pay_per_job  → "Save $X with Pro" (compares pay-per-job spend to Pro monthly)
 *   basic        → "Upgrade to Pro for 2x jobs, lower fees, national reach"
 *   pro          → hides
 *   enterprise   → hides
 *
 * Dismissal sticks for 7 days so it's not spammy, then re-shows.
 */

const DISMISS_KEY = 'upgrade_pro_card_dismissed_at';
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const UpgradeToProCard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [plans, setPlans] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const ts = localStorage.getItem(DISMISS_KEY);
        if (ts && Date.now() - parseInt(ts, 10) < DISMISS_TTL_MS) {
          setDismissed(true);
        }
        const [statusRes, plansRes] = await Promise.all([
          api.get('/monetization/client-subscriptions/status').catch(() => ({ data: {} })),
          api.get('/monetization/client-subscriptions/plans').catch(() => ({ data: {} })),
        ]);
        setStatus(statusRes.data || {});
        setPlans(plansRes.data || {});
      } catch {
        /* noop */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  if (loading || dismissed) return null;

  const plan = (status?.plan_id || 'pay_per_job').toLowerCase();
  if (plan === 'pro' || plan === 'enterprise') return null; // already on top tiers

  const proPlan = (plans?.plans || []).find((p) => p.id === 'pro');
  const payPerJob = plans?.pay_per_job?.price ?? 10.95;

  // Heuristic savings estimate — if they posted 20+ jobs/mo via pay-per-job,
  // Pro ($199) pays back in ~20 posts. Without usage data we still show the
  // pitch, just without the headline number.
  const jobsThisMonth = status?.jobs_this_month ?? null;
  const projectedSavings =
    jobsThisMonth != null && plan === 'pay_per_job'
      ? Math.max(0, jobsThisMonth * payPerJob - (proPlan?.price_monthly || 199))
      : null;

  return (
    <Card
      className="relative overflow-hidden border-0 text-white bg-[linear-gradient(135deg,#0f172a_0%,#0e7490_45%,#06b6d4_100%)] shadow-xl"
      data-testid="upgrade-pro-card"
    >
      <button
        onClick={handleDismiss}
        aria-label="Dismiss"
        className="absolute top-2 right-2 text-white/70 hover:text-white p-1"
        data-testid="upgrade-pro-dismiss"
      >
        <X className="w-4 h-4" />
      </button>

      <CardContent className="p-5 sm:p-6">
        <div className="flex items-start gap-3 mb-3">
          <div className="bg-cyan-400/20 border border-cyan-300/40 rounded-lg p-2 flex-shrink-0">
            <Crown className="w-5 h-5 text-cyan-200" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h3 className="text-base sm:text-lg font-semibold">Unlock iEvntür Pro</h3>
              <Badge className="bg-amber-400 text-slate-900 hover:bg-amber-400 border-0 text-[10px]">
                <Sparkles className="w-3 h-3 mr-0.5" /> 7-day free trial
              </Badge>
            </div>
            {projectedSavings != null && projectedSavings > 0 ? (
              <p className="text-sm text-cyan-100">
                You'd save{' '}
                <strong className="text-white">${projectedSavings.toFixed(0)}</strong> this month
                by switching from pay-per-post to Pro.
              </p>
            ) : plan === 'pay_per_job' ? (
              <p className="text-sm text-cyan-100">
                Posting more than a few gigs a month? Pro pays for itself after ~20 posts — plus
                lower platform fee, national reach, and priority support.
              </p>
            ) : (
              <p className="text-sm text-cyan-100">
                Double your job limit, drop to a 5% platform fee, and reach talent nationally.
              </p>
            )}
          </div>
        </div>

        <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-1.5 text-xs sm:text-sm text-white/90 mb-4">
          <li className="flex items-center gap-1.5">
            <Check className="w-3.5 h-3.5 text-emerald-300" /> 30 job posts / month
          </li>
          <li className="flex items-center gap-1.5">
            <TrendingDown className="w-3.5 h-3.5 text-emerald-300" /> 5% platform fee (vs 7%)
          </li>
          <li className="flex items-center gap-1.5">
            <Globe2 className="w-3.5 h-3.5 text-emerald-300" /> National reach upgrade
          </li>
          <li className="flex items-center gap-1.5">
            <Zap className="w-3.5 h-3.5 text-emerald-300" /> 5 free boosts / month
          </li>
          <li className="flex items-center gap-1.5">
            <Users2 className="w-3.5 h-3.5 text-emerald-300" /> 10 team seats
          </li>
          <li className="flex items-center gap-1.5">
            <Check className="w-3.5 h-3.5 text-emerald-300" /> Priority support
          </li>
        </ul>

        <div className="flex flex-wrap items-end gap-4 justify-between">
          <div>
            <p className="text-xs text-cyan-200/80 uppercase tracking-wide">Pro</p>
            <p className="text-2xl font-semibold leading-none">
              ${proPlan?.price_monthly || 199}
              <span className="text-sm font-normal text-cyan-100 ml-1">/mo</span>
            </p>
            <p className="text-[11px] text-cyan-200/80 mt-1">
              or ${proPlan?.price_yearly || 1999}/yr · ~17% off
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="text-cyan-100 hover:bg-white/10 hover:text-white"
              onClick={() => navigate('/client/settings/subscription')}
              data-testid="upgrade-pro-compare-btn"
            >
              Compare plans
            </Button>
            <Button
              size="sm"
              className="bg-white text-cyan-700 hover:bg-cyan-50 font-semibold shadow-md"
              onClick={() => navigate('/client/settings/subscription?plan=pro&cta=home')}
              data-testid="upgrade-pro-start-btn"
            >
              Start free trial
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default UpgradeToProCard;
